import type { Conversation } from "@rulebase/core/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  Api2RequestError,
  rulebaseApi2Request,
} from "../lib/rulebase-api2-client/client";

interface UseConversationImportOptions {
  ticketId: string;
  enabled?: boolean;
}

interface UseConversationImportReturn {
  conversation: Conversation | undefined;
  isLoading: boolean;
  isImporting: boolean;
  isSearching: boolean;
  error: Error | null;
  retry: () => void;
}

type ConversationListResponse = {
  data: Conversation[];
  page: { next_cursor: string | null; has_more: boolean };
};

export function useConversationImport({
  ticketId,
  enabled = true,
}: UseConversationImportOptions): UseConversationImportReturn {
  const attemptedTicketId = useRef<string | null>(null);
  const getConversationsQuery = useQuery({
    queryKey: ["conversations", { filter: { external_id: ticketId } }],
    queryFn: () => {
      const params = new URLSearchParams({
        limit: "1",
        filter: JSON.stringify({ external_id: ticketId }),
      });
      return rulebaseApi2Request<ConversationListResponse>(
        `/conversations?${params.toString()}`,
      );
    },
    enabled: !!ticketId && enabled,
  });

  const importConversationMutation = useMutation({
    mutationFn: () =>
      rulebaseApi2Request<{ data: { id: string } }>("/conversations/import", {
        method: "POST",
        body: JSON.stringify({
          conversation_import: { external_id: ticketId },
        }),
      }),
    onSettled: (_data, error) => {
      if (
        !error ||
        (error instanceof Api2RequestError && error.status === 409)
      ) {
        getConversationsQuery.refetch();
      }
    },
    onError: (error) => {
      console.error("Failed to import conversation:", error);
    },
  });

  const conversation = getConversationsQuery.data?.data[0];

  useEffect(() => {
    if (
      enabled &&
      ticketId &&
      !getConversationsQuery.isLoading &&
      !conversation &&
      attemptedTicketId.current !== ticketId
    ) {
      attemptedTicketId.current = ticketId;
      importConversationMutation.mutate();
    }
  }, [
    enabled,
    ticketId,
    conversation,
    importConversationMutation,
    getConversationsQuery.isLoading,
  ]);

  return {
    conversation,
    isLoading: getConversationsQuery.isLoading,
    isImporting: importConversationMutation.isPending,
    isSearching: false,
    error:
      getConversationsQuery.error ||
      (conversation ? null : importConversationMutation.error),
    retry: () => {
      attemptedTicketId.current = ticketId;
      importConversationMutation.mutate();
    },
  };
}
